/**
 * Message Parser Utility
 * Handles parsing of JSON-formatted bot messages with buttons
 */

import React from 'react';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

/**
 * Formats WhatsApp-style text into React nodes.
 * Supports: *bold*, _italic_, and clickable URLs
 */
export function formatWhatsAppText(text: string): React.ReactNode {
  if (!text) return text;

  // First split on URLs, then process each non-URL segment for bold/italic
  const urlParts = text.split(URL_REGEX);

  const nodes: React.ReactNode[] = [];

  urlParts.forEach((segment, segIndex) => {
    if (URL_REGEX.test(segment)) {
      // Reset lastIndex after test()
      URL_REGEX.lastIndex = 0;
      nodes.push(
        React.createElement('a', {
          key: `url-${segIndex}`,
          href: segment,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: { textDecoration: 'underline', wordBreak: 'break-all' },
        }, segment)
      );
    } else {
      // Process bold/italic within plain text segments
      const parts = segment.split(/(\*[^*\n]+\*|_[^_\n]+_)/g);
      parts.forEach((part, partIndex) => {
        const key = `text-${segIndex}-${partIndex}`;
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
          nodes.push(React.createElement('strong', { key }, part.slice(1, -1)));
        } else if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
          nodes.push(React.createElement('em', { key }, part.slice(1, -1)));
        } else {
          nodes.push(part);
        }
      });
    }
  });

  URL_REGEX.lastIndex = 0;
  return nodes;
}

export interface ParsedMessage {
  header?: string | null;
  body: string;
  buttons?: string[];
  /** Parallel to `buttons`: the URL a button opens, or null for reply buttons.
   *  WhatsApp rows (merged deep-link threads) store buttons as OBJECTS —
   *  {"type":"URL","text":"Shop now","url":"..."} — where web rows store plain
   *  strings; rendering those objects as React children crashed the thread. */
  buttonUrls?: (string | null)[];
  isJson: boolean;
  isSilent: boolean;
  originalText: string;
}

export interface MessageButton {
  text: string;
  url: string | null;
}

/** Normalize the mixed button shapes (strings from web rows, objects from
 *  WhatsApp interactive/template rows) into {text, url} pairs. Unknown or
 *  empty entries are dropped rather than rendered. */
function normalizeButtons(raw: unknown): MessageButton[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: MessageButton[] = [];
  for (const b of raw) {
    if (typeof b === 'string') {
      if (b.trim()) out.push({ text: b, url: null });
    } else if (b && typeof b === 'object') {
      const anyB = b as any;
      const text = typeof anyB.text === 'string' && anyB.text.trim()
        ? anyB.text
        : typeof anyB.title === 'string' && anyB.title.trim()
          ? anyB.title
          : typeof anyB.label === 'string' && anyB.label.trim()
            ? anyB.label
            : '';
      if (!text) continue;
      const url = typeof anyB.url === 'string' && anyB.url.trim() ? anyB.url : null;
      out.push({ text, url });
    }
  }
  return out.length > 0 ? out : undefined;
}

// Parsing is pure on the message text, and the chat re-renders the same texts
// constantly (every keystroke used to re-JSON.parse the whole thread). Cache
// results by text; capped so an endless session can't grow it unbounded.
const parseCache = new Map<string, ParsedMessage>();
const PARSE_CACHE_MAX = 1000;

/**
 * Parse a message text to extract JSON content (header, body, buttons)
 * Returns parsed content if valid JSON with buttons, otherwise returns original text
 */
export function parseMessageContent(messageText: string): ParsedMessage {
  if (!messageText || typeof messageText !== 'string') {
    return {
      body: messageText,
      isJson: false,
      isSilent: false,
      originalText: messageText,
    };
  }

  const cached = parseCache.get(messageText);
  if (cached) return cached;

  const result = computeParsedMessage(messageText);
  if (parseCache.size >= PARSE_CACHE_MAX) {
    parseCache.delete(parseCache.keys().next().value!);
  }
  parseCache.set(messageText, result);
  return result;
}

function computeParsedMessage(messageText: string): ParsedMessage {
  const defaultResult: ParsedMessage = {
    body: messageText,
    isJson: false,
    isSilent: false,
    originalText: messageText,
  };

  try {
    // Find JSON boundaries
    const jsonStart = messageText.indexOf('{');
    const jsonEnd = messageText.lastIndexOf('}');
    
    if (jsonStart === -1 || jsonEnd === -1 || jsonEnd <= jsonStart) {
      return defaultResult;
    }

    // Extract and parse JSON
    const jsonStr = messageText.substring(jsonStart, jsonEnd + 1);
    const jsonObj = JSON.parse(jsonStr);

    // Silent replies (e.g. {"silent":true,"body":"carousel sent"}) are
    // internal-only — vert persists them so the staff inbox can show them to
    // superadmins, but they must NEVER render in the customer-facing widget.
    const isSilent = jsonObj.silent === true;

    // Validate it has the expected structure
    if (jsonObj.body && typeof jsonObj.body === 'string') {
      const normalized = normalizeButtons(jsonObj.buttons);
      return {
        // Media headers are OBJECTS — only a plain string header may render.
        header: typeof jsonObj.header === 'string' ? jsonObj.header : null,
        body: jsonObj.body,
        buttons: normalized?.map((b) => b.text),
        buttonUrls: normalized?.map((b) => b.url),
        isJson: true,
        isSilent,
        originalText: messageText,
      };
    }

    if (isSilent) {
      return { body: '', isJson: true, isSilent: true, originalText: messageText };
    }

    return defaultResult;
  } catch (error) {
    // Not valid JSON or doesn't match our format
    return defaultResult;
  }
}

/**
 * Get display text from a message (extracts body from JSON or returns original)
 */
export function getMessageDisplayText(messageText: string): string {
  const parsed = parseMessageContent(messageText);
  return parsed.body;
}

/**
 * True if the message is a silent/internal-only reply that must not be shown
 * to the customer in the widget.
 */
export function isSilentMessage(messageText: string): boolean {
  return parseMessageContent(messageText).isSilent;
}

/**
 * Get header text from a JSON message
 */
export function getMessageHeader(messageText: string): string | null {
  const parsed = parseMessageContent(messageText);
  return parsed.isJson ? parsed.header || null : null;
}

/**
 * Get buttons from a JSON message
 */
export function getMessageButtons(messageText: string): string[] | null {
  const parsed = parseMessageContent(messageText);
  return parsed.isJson && parsed.buttons ? parsed.buttons : null;
}

/**
 * Buttons WITH their link targets: URL buttons (WhatsApp template/interactive
 * rows) open their url; reply buttons (url null) send their text.
 */
export function getMessageButtonMeta(messageText: string): MessageButton[] | null {
  const parsed = parseMessageContent(messageText);
  if (!parsed.isJson || !parsed.buttons) return null;
  return parsed.buttons.map((text, i) => ({
    text,
    url: parsed.buttonUrls?.[i] ?? null,
  }));
}

